import React, { useEffect, useState } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCircle, ShieldCheck, Lock, Clock, KeyRound, CheckCircle2 } from "lucide-react";
import { MfaSetup } from "@/components/mfa/MfaSetup";
import { NotaryServiceCounties } from "@/components/server/NotaryServiceCounties";
import { formatApiErrorDetail } from "@/lib/api";

const ROLE_LABEL = { client: "Attorney / Client", server: "Process Server", admin: "Administrator" };

export default function Profile() {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", address: "", firm_name: "" });
  const [newEmail, setNewEmail] = useState("");
  const [newLicense, setNewLicense] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/auth/me").then(({ data }) => {
    setMe(data);
    const fn = data.first_name || (data.name || "").split(" ")[0] || "";
    const ln = data.last_name || (data.name || "").split(" ").slice(1).join(" ") || "";
    setForm({ first_name: fn, last_name: ln, phone: data.phone || "", address: data.address || "", firm_name: data.firm_name || "" });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { first_name: form.first_name, last_name: form.last_name, phone: form.phone, address: form.address };
      if (me?.role === "client") payload.firm_name = form.firm_name;
      await api.post("/auth/profile", payload);
      toast.success("Profile updated");
      await load();
      refresh && refresh();
    } catch (e) { toast.error(extractErrorMessage(e, "Could not save profile")); }
    finally { setSaving(false); }
  };

  const requestEmail = async () => {
    if (!newEmail.trim()) { toast.error("Enter the new email address"); return; }
    try { await api.post("/auth/profile", { email: newEmail.trim() }); toast.success("Email change submitted for admin review"); setNewEmail(""); await load(); }
    catch (e) { toast.error(extractErrorMessage(e, "Could not submit email change")); }
  };

  const requestLicense = async () => {
    if (!newLicense.trim()) { toast.error("Enter the new license number"); return; }
    try { await api.post("/auth/profile", { license_number: newLicense.trim() }); toast.success("License change submitted for admin review"); setNewLicense(""); await load(); }
    catch (e) { toast.error(extractErrorMessage(e, "Could not submit license change")); }
  };

  if (!me) return <div className="p-8 text-slate-400" data-testid="profile-loading">Loading profile…</div>;
  const isServer = me.role === "server";

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="profile-page">
      <div className="flex items-center gap-3">
        <UserCircle className="h-8 w-8 text-emerald-600" />
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">My Profile</h2>
          <p className="text-slate-500">View and update your contact information.</p>
        </div>
      </div>

      <MfaSection />

      {/* Editable profile information */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
          <ShieldCheck className="h-4 w-4 text-emerald-500" /> Editable profile information
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div><Label>First name</Label><Input data-testid="profile-first-name" value={form.first_name} onChange={set("first_name")} /></div>
          <div><Label>Last name</Label><Input data-testid="profile-last-name" value={form.last_name} onChange={set("last_name")} /></div>
          <div><Label>Phone number</Label><Input data-testid="profile-phone" value={form.phone} onChange={set("phone")} /></div>
          {me.role === "client" && <div><Label>Firm name</Label><Input data-testid="profile-firm" value={form.firm_name} onChange={set("firm_name")} /></div>}
          <div className="sm:col-span-2"><Label>Mailing / contact address</Label><Input data-testid="profile-address" value={form.address} onChange={set("address")} /></div>
        </div>
        <Button data-testid="profile-save" onClick={save} disabled={saving} className="mt-5 bg-slate-900 text-white hover:bg-slate-800">{saving ? "Saving…" : "Save changes"}</Button>
      </div>

      {/* Email — verification/security required */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500"><Lock className="h-4 w-4 text-amber-500" /> Sign-in email</h3>
        <p className="text-sm text-slate-600">Current: <b data-testid="profile-current-email">{me.email}</b></p>
        {me.pending_email ? (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="profile-pending-email">
            <Clock className="h-4 w-4" /> Change to <b>{me.pending_email}</b> is pending admin review. Your login stays as {me.email} until approved.
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <Input data-testid="profile-new-email" type="email" placeholder="new-email@example.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="sm:max-w-xs" />
            <Button data-testid="profile-request-email" variant="outline" onClick={requestEmail}>Request email change</Button>
          </div>
        )}
        <p className="mt-2 text-xs text-slate-400">For security, changing your sign-in email requires admin verification and is never applied automatically.</p>
      </div>

      {/* License — service providers, admin-approved */}
      {isServer && (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500"><Lock className="h-4 w-4 text-amber-500" /> License number</h3>
          <p className="text-sm text-slate-600">Current: <b data-testid="profile-current-license">{me.license_number || "—"}</b></p>
          {me.pending_license_number ? (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700" data-testid="profile-pending-license">
              <Clock className="h-4 w-4" /> Change to <b>{me.pending_license_number}</b> is pending admin review.
            </p>
          ) : (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Input data-testid="profile-new-license" placeholder="New license number" value={newLicense} onChange={(e) => setNewLicense(e.target.value)} className="sm:max-w-xs" />
              <Button data-testid="profile-request-license" variant="outline" onClick={requestLicense}>Request license update</Button>
            </div>
          )}
        </div>
      )}

      {/* Service counties — optional, editable any time (reuses provider_service_counties) */}
      {isServer && (
        <NotaryServiceCounties
          title={["notary", "both"].includes(me.service_capability) ? "Counties you prefer to serve" : "Counties you serve"}
          subtitle={["notary", "both"].includes(me.service_capability)
            ? "Optional. Update the counties you prefer for notary work so those jobs reach you first. This never affects your approval."
            : "Optional. Update the counties where you serve process so local jobs reach you first. This never affects your approval."}
        />
      )}

      {/* Read-only account info */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-6" data-testid="profile-readonly">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500"><Lock className="h-4 w-4" /> Account information (not directly editable)</h3>
        <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2">
          <p><b>Role:</b> {ROLE_LABEL[me.role] || me.role}</p>
          {me.role === "client" && <p><b>Account status:</b> {me.client_status || "—"}</p>}
          {isServer && <p><b>Server status:</b> {me.server_status || "—"}</p>}
          {me.agency_name && <p><b>Agency:</b> {me.agency_name}</p>}
          <p><b>Member since:</b> {me.created_at ? new Date(me.created_at).toLocaleDateString() : "—"}</p>
        </div>
        <p className="mt-3 text-xs text-slate-400">Invoices, payments, completed services, attempts, evidence, agreements and audit history reflect the details captured at the time of each transaction and are never changed by editing your profile.</p>
      </div>
    </div>
  );
}

function MfaSection() {
  const [status, setStatus] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);

  const load = () => api.get("/auth/mfa/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const disable = async () => {
    if (!disableCode.trim()) { toast.error("Enter your authenticator or recovery code"); return; }
    setDisabling(true);
    try {
      const { data } = await api.post("/auth/mfa/disable", { code: disableCode.trim() });
      toast.success(data.note || "Two-factor disabled");
      setDisableCode("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not disable MFA");
    } finally { setDisabling(false); }
  };

  if (!status) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="mfa-section">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
        <ShieldCheck className="h-4 w-4 text-emerald-500" /> Two-factor authentication (2FA)
      </h3>

      {status.enabled ? (
        <div className="mt-2 space-y-4">
          <p className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="mfa-status-on">
            <CheckCircle2 className="h-4 w-4" /> 2FA is <b>on</b>. {status.recovery_codes_remaining} recovery code{status.recovery_codes_remaining === 1 ? "" : "s"} remaining.
          </p>
          <div>
            <Label className="flex items-center gap-1.5 text-slate-600"><KeyRound className="h-4 w-4" /> Turn off 2FA</Label>
            <p className="mb-2 mt-0.5 text-xs text-slate-500">
              {status.required
                ? "Your role requires 2FA — you can turn it off, but you’ll be asked to set it up again at your next sign-in."
                : "Enter a current authenticator code (or a recovery code) to confirm."}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input data-testid="mfa-disable-code" placeholder="123456 or recovery code" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} className="sm:max-w-xs" />
              <Button data-testid="mfa-disable-button" variant="outline" onClick={disable} disabled={disabling} className="border-red-200 text-red-600 hover:bg-red-50">
                {disabling ? "Turning off…" : "Turn off 2FA"}
              </Button>
            </div>
          </div>
        </div>
      ) : enrolling ? (
        <div className="mt-3 rounded-lg border border-slate-200 p-4">
          <MfaSetup mode="auth" onComplete={() => { setEnrolling(false); load(); toast.success("Two-factor authentication enabled"); }} />
        </div>
      ) : (
        <div className="mt-2">
          <p className="text-sm text-slate-600">
            Add a second layer of security using an authenticator app.
            {status.required && <span className="font-semibold text-amber-700"> Your role requires 2FA.</span>}
          </p>
          <Button data-testid="mfa-enable-button" onClick={() => setEnrolling(true)} className="mt-3 bg-slate-900 text-white hover:bg-slate-800">
            Enable 2FA
          </Button>
        </div>
      )}
    </div>
  );
}

